import React, { useState, useEffect, useContext } from 'react';
import { Table, Button, Card, Row, Col, Badge, Form, InputGroup, Alert, Spinner, Modal, Pagination } from 'react-bootstrap';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import AuthContext from '../../context/AuthContext';
import { getDoctorFilterParams } from '../../utils/doctorFilter';
import { FaPlus, FaSearch, FaFileInvoiceDollar, FaEye, FaCreditCard, FaSync, FaTrashAlt } from 'react-icons/fa';

const Billing = () => {
  const { user } = useContext(AuthContext);
  const location = useLocation();
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [billToDelete, setBillToDelete] = useState(null);
  const [queueRecords, setQueueRecords] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  
  const [patients, setPatients] = useState([]);
  const [patientSearchResults, setPatientSearchResults] = useState([]);
  const [showPatientSearch, setShowPatientSearch] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);

  // Get permissions
  const permissions = user?.role === 'admin' ? {} : (user?.permissions || {});
  const isAdmin = user?.role === 'admin';
  const hasPermission = (key) => isAdmin || permissions[key] === true;

  useEffect(() => {
    fetchBills();
    fetchPatients();
    fetchQueueRecords();
    
    if (location.state?.notification) {
      setError(location.state.notification);
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  const fetchBills = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const doctorFilter = getDoctorFilterParams(user);
      const params = { ...doctorFilter };
      
      if (filterStatus) params.status = filterStatus;
      if (dateFrom && dateTo) {
        params.from = dateFrom;
        params.to = dateTo;
      }
      if (selectedPatient) params.patientId = selectedPatient.id;
      
      const res = await axios.get('/api/billings.php', { params });
      
      if (res.data && Array.isArray(res.data)) {
        setBills(res.data);
      } else {
        setBills([]);
        setError('Received invalid data format from server');
      }
    } catch (err) {
      console.error('Error fetching bills:', err);
      setError({ type: 'danger', message: 'Failed to load billing data. Please try again later.' });
      setBills([]);
    } finally {
      setLoading(false);
    }
  };
  
  const fetchPatients = async () => {
    try {
      const res = await axios.get('/api/patients.php');
      setPatients(res.data);
    } catch (err) {
      console.error('Error fetching patients:', err);
    }
  };
  
  const fetchQueueRecords = async () => {
    try {
      const res = await axios.get('/api/queue.php', {
        params: { allRecords: true }
      });
      setQueueRecords(res.data);
    } catch (err) {
      console.error('Error fetching queue records:', err);
    }
  };

  const handlePatientSearch = (e) => {
    const searchTerm = e.target.value;
    setSearchTerm(searchTerm);
    
    if (!searchTerm.trim()) {
      setSelectedPatient(null);
      setShowPatientSearch(false);
      return;
    }
    
    const results = patients.filter(patient => 
      patient.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (patient.patientId && patient.patientId.toLowerCase().includes(searchTerm.toLowerCase()))
    );
    
    setPatientSearchResults(results);
    setShowPatientSearch(true);
  };
  
  const handlePatientSelect = (patient) => {
    setSelectedPatient(patient);
    setSearchTerm(`${patient.name} (ID: ${patient.patientId || 'N/A'})`);
    setShowPatientSearch(false);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchBills();
  };

  const handleReset = () => {
    setSearchTerm('');
    setFilterStatus('');
    setDateFrom('');
    setDateTo('');
    setSelectedPatient(null);
    fetchBills();
  };

  const filteredBills = bills.filter(bill => {
    if (selectedPatient) return true;
    if (!searchTerm.trim()) return true;
    
    const patientName = bill.Patient?.name?.toLowerCase() || '';
    const billNumber = bill.billNumber?.toLowerCase() || '';
    const search = searchTerm.toLowerCase();
    
    return patientName.includes(search) || billNumber.includes(search);
  });

  // Pagination
  const totalPages = Math.ceil(filteredBills.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentBills = filteredBills.slice(startIndex, endIndex);

  // Reset to page 1 when filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [filteredBills.length]);

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <Badge bg="warning">Pending</Badge>;
      case 'partial':
        return <Badge bg="info">Partial</Badge>;
      case 'complete':
        return <Badge bg="success">Complete</Badge>;
      default:
        return <Badge bg="secondary">Unknown</Badge>;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const formatCurrency = (amount) => {
    return parseFloat(amount).toLocaleString('en-US', {
      style: 'currency',
      currency: 'PKR'
    });
  };
  
  const handleDeleteClick = (bill) => {
    setBillToDelete(bill);
    setShowDeleteModal(true);
  };
  
  const handleDeleteConfirm = async () => {
    if (!billToDelete) return;
    
    try {
      setLoading(true);
      await axios.delete(`/api/billings.php?id=${billToDelete.id}`);
      
      setBills(prevBills => prevBills.filter(b => b.id !== billToDelete.id));
      setShowDeleteModal(false);
      setBillToDelete(null);
      setError({ type: 'success', message: `Bill ${billToDelete.billNumber} has been deleted successfully.` });
    } catch (err) {
      console.error('Error deleting bill:', err);
      setError({ type: 'danger', message: `Failed to delete bill. ${err.response?.data?.msg || 'Please try again.'}` });
    } finally {
      setLoading(false);
    }
  };
  
  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setBillToDelete(null);
  };

  return (
    <div className="billing-page">
      <Card className="mb-4">
        <Card.Body>
          <Row className="align-items-center mb-3">
            <Col md={6}>
              <h2><FaFileInvoiceDollar className="me-2" /> Billing Management</h2>
            </Col>
            <Col md={6} className="text-md-end">
              <Button 
                variant="outline-primary" 
                className="me-2" 
                onClick={fetchBills} 
                disabled={loading}
              >
                <FaSync className={loading ? "spin-animation me-1" : "me-1"} /> Refresh
              </Button>
              {hasPermission('createBill') && (
                <Link to="/create-bill" className="btn btn-primary">
                  <FaPlus className="me-1" /> Create New Bill
                </Link>
              )}
            </Col>
          </Row>

          {error && (
            <Alert variant={error.type || "danger"} className="d-flex justify-content-between align-items-center">
              <span>{error.message || error}</span>
              <Button variant={`outline-${error.type || "danger"}`} size="sm" onClick={() => setError(null)}>
                Dismiss
              </Button>
            </Alert>
          )}
          
          <Form onSubmit={handleSearch}>
            <Row className="mb-3">
              <Col md={4} className="mb-2 position-relative">
                <InputGroup>
                  <InputGroup.Text><FaSearch /></InputGroup.Text>
                  <Form.Control
                    placeholder="Search by patient name or ID"
                    value={searchTerm}
                    onChange={handlePatientSearch}
                    autoComplete="off"
                  />
                  {selectedPatient && (
                    <Button 
                      variant="outline-secondary" 
                      onClick={() => {
                        setSelectedPatient(null);
                        setSearchTerm('');
                      }}
                    >
                      Clear
                    </Button>
                  )}
                </InputGroup>
                {showPatientSearch && patientSearchResults.length > 0 && (
                  <div className="position-absolute w-100 mt-1 z-index-dropdown shadow">
                    <ul className="list-group" style={{ maxHeight: '200px', overflowY: 'auto', zIndex: 1000 }}>
                      {patientSearchResults.map(patient => (
                        <li 
                          key={patient.id}
                          className="list-group-item list-group-item-action"
                          onClick={() => handlePatientSelect(patient)}
                          style={{ cursor: 'pointer' }}
                        >
                          <div className="d-flex justify-content-between">
                            <div>
                              <strong>{patient.name}</strong>
                              <div className="small text-muted">
                                ID: {patient.patientId || 'N/A'}, Contact: {patient.contactNumber || 'N/A'}
                              </div>
                            </div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </Col>
              <Col md={3} className="mb-2">
                <Form.Select 
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                >
                  <option value="">Filter by status</option>
                  <option value="pending">Pending</option>
                  <option value="partial">Partially Paid</option>
                  <option value="complete">Complete</option>
                </Form.Select>
              </Col>
              <Col md={2} className="mb-2">
                <Form.Control
                  type="date"
                  placeholder="From"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                />
              </Col>
              <Col md={2} className="mb-2">
                <Form.Control
                  type="date"
                  placeholder="To"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                />
              </Col>
              <Col md={1} className="d-flex mb-2">
                <Button type="submit" variant="primary" className="me-1" disabled={loading}>
                  <FaSearch />
                </Button>
                <Button type="button" variant="outline-secondary" onClick={handleReset} disabled={loading}>
                  Reset
                </Button>
              </Col>
            </Row>
          </Form>

          {loading ? (
            <div className="text-center my-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-2">Loading billing data...</p>
            </div>
          ) : filteredBills.length > 0 ? (
            <>
              <Table responsive hover>
                <thead>
                  <tr>
                    <th>Bill #</th>
                    <th>Patient</th>
                    <th>Date</th>
                    <th>Amount</th>
                    <th>Paid</th>
                    <th>Balance</th>
                    <th>Payment Status</th>
                    <th>Bill Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {currentBills.map(bill => (
                    <tr key={bill.id}>
                      <td>{bill.billNumber}</td>
                      <td>{bill.patientName || bill.patient || bill.Patient?.name || 'N/A'}</td>
                      <td>{formatDate(bill.date || bill.billingDate || bill.createdAt)}</td>
                      <td>{formatCurrency(bill.totalAmount)}</td>
                      <td>{formatCurrency(bill.paidAmount)}</td>
                      <td>{formatCurrency(bill.totalAmount - bill.paidAmount)}</td>
                      <td>{getStatusBadge(bill.paymentStatus)}</td>
                      <td>
                        {(() => {
                          const isRefunded = bill.notes && bill.notes.includes('[REFUND:');
                          if (isRefunded) return <Badge bg="warning">Cancelled</Badge>;
                          
                          // Check if bill is used based on billStatus field from database
                          if (bill.billStatus === 'used') {
                            return <Badge bg="danger">Used</Badge>;
                          } else if (bill.billStatus === 'not-used' || !bill.billStatus) {
                            return <Badge bg="success">Not Used</Badge>;
                          }
                          
                          return <Badge bg="secondary">Unknown</Badge>;
                        })()}
                      </td>
                      <td>
                        {hasPermission('viewBill') && (
                          <Link 
                            to={`/billing/${bill.id}`} 
                            className="btn btn-sm btn-outline-primary me-1"
                            title="View Details"
                          >
                            <FaEye />
                          </Link>
                        )}
                        {hasPermission('payment') && bill.paymentStatus !== 'complete' && (
                          <Link 
                            to={`/billing/${bill.id}/payment`} 
                            className="btn btn-sm btn-outline-success me-1"
                            title="Process Payment"
                          >
                            <FaCreditCard />
                          </Link>
                        )}
                        {hasPermission('deleteBill') && (
                          <Button
                            variant="outline-danger"
                            size="sm"
                            title="Delete Bill"
                            onClick={() => handleDeleteClick(bill)}
                          >
                            <FaTrashAlt />
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              
              {filteredBills.length > itemsPerPage && (
                <div className="d-flex justify-content-center mt-3">
                  <Pagination>
                    <Pagination.First onClick={() => setCurrentPage(1)} disabled={currentPage === 1} />
                    <Pagination.Prev onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} disabled={currentPage === 1} />
                    
                    {[...Array(totalPages)].map((_, index) => (
                      <Pagination.Item
                        key={index + 1}
                        active={index + 1 === currentPage}
                        onClick={() => setCurrentPage(index + 1)}
                      >
                        {index + 1}
                      </Pagination.Item>
                    ))}
                    
                    <Pagination.Next onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))} disabled={currentPage === totalPages} />
                    <Pagination.Last onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} />
                  </Pagination>
                </div>
              )}
            </>
          ) : (
            <Alert variant="info" className="text-center my-4">
              No billing records found. Create a new bill to get started.
            </Alert>
          )}
        </Card.Body>
      </Card>
      
      <Modal show={showDeleteModal} onHide={handleDeleteCancel} centered backdrop="static">
        <Modal.Header>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete bill {billToDelete?.billNumber}?</p>
          <p className="text-danger">
            <strong>Warning:</strong> This action cannot be undone. All data related to this bill will be permanently deleted.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleDeleteCancel} disabled={loading}>
            Cancel
          </Button>
          <Button 
            variant="danger" 
            onClick={handleDeleteConfirm}
            disabled={loading}
          >
            {loading ? 'Deleting...' : 'Delete Bill'}
          </Button>
        </Modal.Footer>
      </Modal>
      
      <style jsx="true">{`
        .spin-animation {
          animation: spin 1s linear infinite;
        }
        
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        
        .z-index-dropdown {
          z-index: 1050;
        }
      `}</style>
    </div>
  );
};

export default Billing;


